/** Fixtures de recette de l’assistant, activables par ?demo=1&modules=tous. */
import {
  modificationAssistantSchema,
  reglagesAssistantSchema,
  OBJECTIFS_BOITE_PAR_DEFAUT,
} from '@artosera/shared';
import type { Conversation, Message, ReglagesBoite } from './boite-reception.js';
import type { Essai, Reglages, Relance, TableauAssistant } from './assistant-reponse.js';
import { ApiError } from './errors.js';

let reglages: Reglages = { ...reglagesAssistantSchema.parse({}), version: 0 };

const date = '2026-10-18T10:00:00.000Z';

const tri: NonNullable<Conversation['assistant']> = {
  categorieProposee: 'rendez_vous',
  urgence: 'haute',
  sensible: false,
  motifsSensibles: [],
  pratique: true,
  sujetsPratiques: ['horaires'],
  langue: 'fr',
  resume: 'Une visite de l’exposition est souhaitée cette semaine.',
  trieLe: date,
  etat: 'trie',
};

const conversations: Conversation[] = [
  {
    id: '000000000000000000000001',
    canal: 'email',
    sujet: 'Visite de l’exposition',
    correspondant: { nom: 'Camille Durand', adresse: 'camille@example.test' },
    contactId: null,
    oeuvreIds: [],
    demandeId: null,
    etat: 'ouverte',
    enAttenteJusquau: null,
    automatique: false,
    categorie: 'rendez_vous',
    etiquettes: [],
    assigneeA: null,
    nonLue: true,
    dernierEntrantLe: date,
    dernierMessageLe: date,
    nombreMessages: 1,
    contactEfface: false,
    version: 0,
    extrait: 'Bonjour, pouvons-nous visiter l’exposition cette semaine ?',
    enRetard: false,
    assistant: tri,
    categorieSource: 'assistant',
    sensible: false,
    motifsSensibles: [],
  },
];

conversations.push({
  ...conversations[0]!,
  id: '000000000000000000000002',
  sujet: 'Une négociation à examiner',
  assistant: {
    ...tri,
    sensible: true,
    motifsSensibles: ['negociation'],
    resume: 'Une remise est demandée.',
    urgence: 'normale',
  },
  sensible: true,
  motifsSensibles: ['negociation'],
});
conversations.push({
  ...conversations[0]!,
  id: '000000000000000000000003',
  sujet: 'Proposition commerciale',
  assistant: {
    ...tri,
    categorieProposee: 'indesirable',
    urgence: 'basse',
    etat: 'echec',
    resume: '',
  },
});

const messages: Message[] = conversations.map((c) => ({
  id: `entrant-${c.id}`,
  conversationId: c.id,
  sens: 'entrant',
  etat: 'recu',
  de: c.correspondant,
  texte: c.extrait,
  creeLe: date,
  erreurEnvoi: null,
  htmlDisponible: false,
  tronque: false,
  piecesJointes: [],
  suivi: null,
}));

messages.push({
  ...messages[0]!,
  id: 'automatique-demo',
  sens: 'sortant',
  etat: 'envoye',
  auteurType: 'SYSTEME',
  de: { nom: 'Galerie', adresse: '' },
  texte: 'Réponse automatique — un membre de la galerie reviendra vers vous si besoin.',
  assistant: {
    appelId: 'demo',
    prepareLe: date,
    aVerifier: [],
    oeuvresCitees: [],
    reponsePratique: true,
  },
});

let relances: Relance[] = [
  {
    _id: 'relance-demo',
    type: 'conversation',
    objetId: conversations[0]!.id,
    conversationId: conversations[0]!.id,
    contactId: 'contact-demo',
    proposeeLe: date,
    etat: 'proposee',
    version: 0,
  },
];

function brouillon(c: Conversation) {
  if (c.sensible)
    throw new ApiError(409, 'conflit.version', 'Sensible', { raison: 'conversation_sensible' });
  if (messages.some((m) => m.conversationId === c.id && m.etat === 'brouillon'))
    throw new ApiError(409, 'conflit.version', 'Brouillon existant', {
      raison: 'brouillon_existant',
    });

  const message: Message = {
    ...messages[0]!,
    id: `brouillon-${c.id}`,
    conversationId: c.id,
    sens: 'sortant',
    etat: 'brouillon',
    texte:
      'Bonjour, nous serons heureux de vous accueillir à la galerie. / Hello, we look forward to welcoming you to the gallery.',
    assistant: {
      appelId: 'demo',
      prepareLe: date,
      oeuvresCitees: [],
      aVerifier: ['Confirmer les horaires de visite. / Confirm visiting hours.'],
    },
  };
  messages.push(message);
  return { message };
}

export function reponseDemoAssistant(
  chemin: string,
  params: URLSearchParams,
  methode: string,
  corps?: unknown,
): unknown {
  const entree = corps as
    { version?: number; motif?: string; texte?: string; conversationId?: string } | undefined;

  if (chemin === '/assistant-reponse/reglages') {
    if (methode === 'GET') return structuredClone(reglages);

    const r = modificationAssistantSchema.parse(corps);
    if (r.version !== reglages.version)
      throw new ApiError(409, 'conflit.version', 'Version périmée');
    if (
      r.reponsesPratiques.actif &&
      !reglages.reponsesPratiques.actif &&
      !r.confirmationAutomatique
    )
      throw new ApiError(400, 'validation.invalide', 'Confirmation requise');

    const { confirmationAutomatique: _confirmation, ...valeurs } = r;
    reglages = { ...valeurs, version: r.version + 1 };
    return structuredClone(reglages);
  }
  if (chemin === '/assistant-reponse/tableau')
    return {
      jours: 30,
      messagesTries: 125,
      brouillonsPrepares: 42,
      partSansModification: 0.6,
      reponsesAutomatiques: 8,
      conversationsSensibles: 4,
      echecs: { indisponible: 2 },
    } satisfies TableauAssistant;
  if (chemin === '/assistant-reponse/essai')
    return {
      tri: { ...tri, categorie: 'rendez_vous' },
      sensible: false,
      motifsSensibles: [],
      refus: null,
      brouillon: {
        texte: 'Merci pour votre message. / Thank you for your message.',
        langue: 'fr',
        aVerifier: ['Confirmer les horaires. / Confirm opening hours.'],
        oeuvresCitees: [],
      },
    } satisfies Essai;
  if (chemin === '/assistant-reponse/relances')
    return { relances: structuredClone(relances), page: 1, total: relances.length };
  if (chemin.startsWith('/assistant-reponse/relances/')) {
    const p = relances.find((p) => chemin.includes(`/${p._id}/`));
    if (!p) throw new ApiError(404, 'ressource.introuvable', 'Proposition introuvable');
    if (entree?.version !== p.version)
      throw new ApiError(409, 'conflit.version', 'Version périmée');
    if (chemin.endsWith('/ecarter')) {
      relances = relances.filter((x) => x !== p);
      return {};
    }

    const r = brouillon(conversations[0]!);
    p.etat = 'preparee';
    p.version++;
    return r;
  }

  const geste =
    /^\/assistant-reponse\/conversations\/([^/]+)\/(trier|brouillon|lever-sensible)$/.exec(chemin);
  if (geste) {
    const c = conversations.find((c) => c.id === geste[1]);
    if (!c) throw new ApiError(404, 'ressource.introuvable', 'Conversation introuvable');
    if (entree?.version !== c.version)
      throw new ApiError(409, 'conflit.version', 'Version périmée');
    if (geste[2] === 'brouillon') return brouillon(c);
    if (geste[2] === 'lever-sensible') {
      c.sensible = false;
      c.motifsSensibles = [];
    } else c.assistant = { ...tri };
    c.version++;
    return { conversation: structuredClone(c) };
  }
  if (chemin === '/boite-reception/compteurs') return { aTraiter: 3, nonLues: 2, aMoi: 0 };
  if (chemin === '/boite-reception/reglages')
    return {
      reglages: {
        version: 0,
        adresseReponse: 'reception',
        signature: 'Galerie',
        etiquettes: [],
        objectifsHeures: OBJECTIFS_BOITE_PAR_DEFAUT,
        suiviOuverture: false,
        expediteursIgnores: [],
        adresseReception: 'galerie@recu.example.test',
        receptionDisponible: true,
        dernierRecuLe: date,
      } satisfies ReglagesBoite,
    };
  if (chemin === '/boite-reception/preferences')
    return { preferences: { resumeQuotidien: true, alertes: true } };
  if (chemin === '/boite-reception/conversations') {
    const q = (params.get('q') ?? '').toLowerCase();

    const liste = conversations.filter(
      (c) =>
        (params.get('urgentes') !== 'true' || c.assistant?.urgence === 'haute') &&
        `${c.sujet} ${c.correspondant.nom}`.toLowerCase().includes(q),
    );
    return {
      conversations: structuredClone(liste),
      pagination: { page: 1, pages: 1, total: liste.length },
    };
  }

  const fiche = /^\/boite-reception\/conversations\/([^/]+)$/.exec(chemin);
  if (fiche) {
    const c = conversations.find((c) => c.id === fiche[1]);
    if (!c) throw new ApiError(404, 'ressource.introuvable', 'Conversation introuvable');
    if (methode === 'PATCH') {
      c.nonLue = false;
      c.version++;
    }
    return {
      conversation: structuredClone(c),
      messages: structuredClone(messages.filter((m) => m.conversationId === c.id)),
    };
  }
  return undefined;
}
